import React, {
	useState,
	forwardRef,
	useImperativeHandle,
	useEffect,
} from "react";
import {
	format,
	startOfMonth,
	endOfMonth,
	eachDayOfInterval,
	isSameMonth,
	isToday,
	isSameDay,
	addMonths,
	subMonths,
	startOfWeek,
	endOfWeek,
	isSameMonth as isSameMonthFn,
	setHours,
	setMinutes,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MonthPreview = forwardRef(({ onDateSelect, selectedDate }, ref) => {
	const [currentMonth, setCurrentMonth] = useState(new Date());
	const start = startOfWeek(startOfMonth(currentMonth));
	const end = endOfWeek(endOfMonth(currentMonth));
	const days = eachDayOfInterval({ start, end });
	const today = new Date();
	const isCurrentMonth = isSameMonthFn(currentMonth, today);

	// Add effect to auto-switch month based on selectedDate
	useEffect(() => {
		if (selectedDate && !isSameMonth(selectedDate, currentMonth)) {
			setCurrentMonth(startOfMonth(selectedDate));
		}
	}, [selectedDate]);

	useImperativeHandle(ref, () => ({
		resetToToday: () => {
			const now = new Date();
			setCurrentMonth(now);
			if (onDateSelect) {
				onDateSelect(now);
			}
		},
	}));

	const handlePreviousMonth = () => {
		setCurrentMonth((prev) => subMonths(prev, 1));
	};

	const handleNextMonth = () => {
		setCurrentMonth((prev) => addMonths(prev, 1));
	};

	const handleReturnToCurrent = () => {
		const now = new Date();
		setCurrentMonth(now);
		if (onDateSelect) {
			onDateSelect(now);
		}
	};

	const handleDateClick = (day) => {
		// Set the time to noon (12:00) of the selected day
		const selectedDateWithTime = setMinutes(setHours(day, 12), 0);
		if (onDateSelect) {
			onDateSelect(selectedDateWithTime);
		}
	};

	return (
		<div className="p-4">
			<div className="flex items-center justify-between mb-4">
				<button
					onClick={handlePreviousMonth}
					className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors duration-200"
					aria-label="Previous month"
				>
					<ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
				</button>

				<button
					onClick={handleReturnToCurrent}
					className="relative px-3 py-1 rounded-lg group"
				>
					<div
						className={`
              absolute inset-0 rounded-lg border-2 transition-opacity duration-300
              ${isCurrentMonth ? "opacity-0" : "opacity-100 border-blue-500/50"}
            `}
					/>

					<h3 className="text-lg font-semibold text-gray-900 dark:text-white relative z-10">
						{format(currentMonth, "MMMM yyyy")}
					</h3>
				</button>

				<button
					onClick={handleNextMonth}
					className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors duration-200"
					aria-label="Next month"
				>
					<ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
				</button>
			</div>

			<div className="grid grid-cols-7 gap-1">
				{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
					<div
						key={day}
						className="text-center text-sm text-gray-500 dark:text-gray-400"
					>
						{day}
					</div>
				))}
				{days.map((day) => {
					const isSelected = selectedDate && isSameDay(day, selectedDate);
					const isCurrentDay = isToday(day);
					const isInCurrentMonth = isSameMonth(day, currentMonth);

					return (
						<div
							key={day.toISOString()}
							className={`
                relative text-center p-2 rounded-full
                ${isCurrentDay ? "bg-blue-600 text-white" : ""}
                ${
									!isInCurrentMonth
										? "text-gray-400 dark:text-gray-600"
										: "text-gray-900 dark:text-white"
								}
                ${
									!isCurrentDay && isInCurrentMonth
										? "hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
										: ""
								}
                ${
									isSelected && !isCurrentDay
										? "ring-2 ring-gray-300 dark:ring-gray-600"
										: ""
								}
              `}
							onClick={() => handleDateClick(day)}
						>
							{format(day, "d")}
						</div>
					);
				})}
			</div>
		</div>
	);
});

export default MonthPreview;
